import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  FormGroup,
  FormLabel,
} from "@mui/material";
import {
  ExpandMore,
  Add,
  Delete,
  Email,
  Notifications,
  Phone,
  Telegram,
  Web,
} from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_USER_NOTIFICATION_PREFERENCES,
  GET_NOTIFICATION_CHANNEL_STATUS,
  UPDATE_USER_NOTIFICATION_PREFERENCES,
  UPDATE_NOTIFICATION_SETTINGS,
  GET_NOTIFICATION_THRESHOLDS,
} from "../graphql/notifications";

interface NotificationSettingsProps {
  open: boolean;
  onClose: () => void;
  userId: string;
}

interface NotificationPreferences {
  notificationEnabled: boolean;
  emailNotifications: boolean;
  pushbulletNotifications: boolean;
  webPushNotifications: boolean;
  smsNotifications: boolean;
  telegramNotifications: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  timezone: string;
  notificationSettings?: {
    emailThresholds: number[];
    pushbulletThresholds: number[];
    webPushThresholds: number[];
    smsThresholds: number[];
    telegramThresholds: number[];
    maxNotificationsPerDay: number;
    notificationCooldown: number;
    digestEnabled: boolean;
    digestFrequency: string;
  };
}

const CHANNEL_ICONS = {
  email: <Email />,
  pushbullet: <Notifications />,
  webPush: <Web />,
  sms: <Phone />,
  telegram: <Telegram />,
};

const CHANNEL_NAMES = {
  email: "Email",
  pushbullet: "Pushbullet",
  webPush: "Web Push",
  sms: "SMS",
  telegram: "Telegram",
};

export default function NotificationSettings({
  open,
  onClose,
  userId,
}: NotificationSettingsProps) {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    notificationEnabled: true,
    emailNotifications: true,
    pushbulletNotifications: false,
    webPushNotifications: false,
    smsNotifications: false,
    telegramNotifications: false,
    quietHoursEnabled: false,
    quietHoursStart: "22:00",
    quietHoursEnd: "08:00",
    timezone: "UTC",
    notificationSettings: {
      emailThresholds: [60, 30, 15, 5],
      pushbulletThresholds: [30, 15, 5],
      webPushThresholds: [15, 5],
      smsThresholds: [60, 30],
      telegramThresholds: [30, 15, 5],
      maxNotificationsPerDay: 50,
      notificationCooldown: 5,
      digestEnabled: false,
      digestFrequency: "daily",
    },
  });

  const [channelStatus, setChannelStatus] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: preferencesData, loading: preferencesLoading } = useQuery(
    GET_USER_NOTIFICATION_PREFERENCES,
    {
      variables: { userId },
      skip: !open,
    },
  );

  const { data: channelStatusData } = useQuery(
    GET_NOTIFICATION_CHANNEL_STATUS,
    {
      skip: !open,
    },
  );

  const [updatePreferences] = useMutation(UPDATE_USER_NOTIFICATION_PREFERENCES);
  const [updateSettings] = useMutation(UPDATE_NOTIFICATION_SETTINGS);

  useEffect(() => {
    if (preferencesData?.getUserNotificationPreferences) {
      setPreferences(preferencesData.getUserNotificationPreferences);
    }
  }, [preferencesData]);

  useEffect(() => {
    if (channelStatusData?.getNotificationChannelStatus) {
      setChannelStatus(channelStatusData.getNotificationChannelStatus);
    }
  }, [channelStatusData]);

  const handleChannelToggle = (channel: string, checked: boolean) => {
    setPreferences((prev) => ({
      ...prev,
      [`${channel}Notifications`]: checked,
    }));
  };

  const handleQuietHoursChange = (field: "start" | "end", value: string) => {
    setPreferences((prev) => ({
      ...prev,
      [`quietHours${field.charAt(0).toUpperCase() + field.slice(1)}`]: value,
    }));
  };

  const handleThresholdChange = (channel: string, thresholds: number[]) => {
    setPreferences((prev) => ({
      ...prev,
      notificationSettings: {
        ...prev.notificationSettings!,
        [`${channel}Thresholds`]: thresholds,
      },
    }));
  };

  const addThreshold = (channel: string) => {
    const currentThresholds =
      (preferences.notificationSettings?.[
        `${channel}Thresholds` as keyof typeof preferences.notificationSettings
      ] as number[]) || [];
    const newThreshold = Math.max(...currentThresholds, 0) + 15;
    handleThresholdChange(channel, [...currentThresholds, newThreshold]);
  };

  const removeThreshold = (channel: string, index: number) => {
    const currentThresholds =
      (preferences.notificationSettings?.[
        `${channel}Thresholds` as keyof typeof preferences.notificationSettings
      ] as number[]) || [];
    const newThresholds = currentThresholds.filter((_, i) => i !== index);
    handleThresholdChange(channel, newThresholds);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Update user preferences
      await updatePreferences({
        variables: {
          userId,
          data: {
            notificationEnabled: preferences.notificationEnabled,
            emailNotifications: preferences.emailNotifications,
            pushbulletNotifications: preferences.pushbulletNotifications,
            webPushNotifications: preferences.webPushNotifications,
            smsNotifications: preferences.smsNotifications,
            telegramNotifications: preferences.telegramNotifications,
            quietHoursEnabled: preferences.quietHoursEnabled,
            quietHoursStart: preferences.quietHoursStart,
            quietHoursEnd: preferences.quietHoursEnd,
            timezone: preferences.timezone,
          },
        },
      });

      // Update notification settings
      await updateSettings({
        variables: {
          userId,
          data: {
            emailThresholds: preferences.notificationSettings?.emailThresholds,
            pushbulletThresholds:
              preferences.notificationSettings?.pushbulletThresholds,
            webPushThresholds:
              preferences.notificationSettings?.webPushThresholds,
            smsThresholds: preferences.notificationSettings?.smsThresholds,
            telegramThresholds:
              preferences.notificationSettings?.telegramThresholds,
            maxNotificationsPerDay:
              preferences.notificationSettings?.maxNotificationsPerDay,
            notificationCooldown:
              preferences.notificationSettings?.notificationCooldown,
            digestEnabled: preferences.notificationSettings?.digestEnabled,
            digestFrequency: preferences.notificationSettings?.digestFrequency,
          },
        },
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const getChannelStatus = (channel: string) => {
    return channelStatus[channel] ? "Available" : "Not Configured";
  };

  if (preferencesLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography>Loading notification settings...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Notification Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Settings saved successfully!
            </Alert>
          )}

          {/* Global Notification Toggle */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.notificationEnabled}
                  onChange={(e) =>
                    setPreferences((prev) => ({
                      ...prev,
                      notificationEnabled: e.target.checked,
                    }))
                  }
                />
              }
              label="Enable Notifications"
            />
          </FormControl>

          {/* Channel Status */}
          <Typography variant="h6" gutterBottom>
            Channel Status
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(CHANNEL_NAMES).map(([key, name]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {CHANNEL_ICONS[key as keyof typeof CHANNEL_ICONS]}
                  <Typography variant="body2">{name}</Typography>
                  <Chip
                    label={getChannelStatus(key)}
                    size="small"
                    color={channelStatus[key] ? "success" : "default"}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Channel Toggles */}
          <Typography variant="h6" gutterBottom>
            Notification Channels
          </Typography>
          <FormGroup>
            <Grid container spacing={2}>
              {Object.entries(CHANNEL_NAMES).map(([key, name]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          preferences[
                            `${key}Notifications` as keyof NotificationPreferences
                          ] as boolean
                        }
                        onChange={(e) =>
                          handleChannelToggle(key, e.target.checked)
                        }
                        disabled={!channelStatus[key]}
                      />
                    }
                    label={name}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>

          <Divider sx={{ my: 2 }} />

          {/* Quiet Hours */}
          <Typography variant="h6" gutterBottom>
            Quiet Hours
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={preferences.quietHoursEnabled}
                onChange={(e) =>
                  setPreferences((prev) => ({
                    ...prev,
                    quietHoursEnabled: e.target.checked,
                  }))
                }
              />
            }
            label="Enable Quiet Hours"
          />
          {preferences.quietHoursEnabled && (
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                value={preferences.quietHoursStart}
                onChange={(e) =>
                  handleQuietHoursChange("start", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Time"
                type="time"
                value={preferences.quietHoursEnd}
                onChange={(e) => handleQuietHoursChange("end", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Notification Thresholds */}
          <Typography variant="h6" gutterBottom>
            Event Notification Thresholds
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure when to receive notifications before events (in minutes)
          </Typography>

          {Object.entries(CHANNEL_NAMES).map(([channel, name]) => (
            <Accordion key={channel} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {CHANNEL_ICONS[channel as keyof typeof CHANNEL_ICONS]}
                  <Typography>{name} Thresholds</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">
                      Thresholds (minutes):
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => addThreshold(channel)}
                      disabled={
                        !preferences[
                          `${channel}Notifications` as keyof NotificationPreferences
                        ]
                      }
                    >
                      <Add />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {preferences.notificationSettings?.[
                      `${channel}Thresholds` as keyof typeof preferences.notificationSettings
                    ]?.map((threshold, index) => (
                      <Chip
                        key={index}
                        label={`${threshold} min`}
                        onDelete={() => removeThreshold(channel, index)}
                        deleteIcon={<Delete />}
                        disabled={
                          !preferences[
                            `${channel}Notifications` as keyof NotificationPreferences
                          ]
                        }
                      />
                    )) || []}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          <Divider sx={{ my: 2 }} />

          {/* Advanced Settings */}
          <Typography variant="h6" gutterBottom>
            Advanced Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Notifications Per Day"
                type="number"
                value={
                  preferences.notificationSettings?.maxNotificationsPerDay || 50
                }
                onChange={(e) =>
                  setPreferences((prev) => ({
                    ...prev,
                    notificationSettings: {
                      ...prev.notificationSettings!,
                      maxNotificationsPerDay: parseInt(e.target.value) || 50,
                    },
                  }))
                }
                inputProps={{ min: 1, max: 1000 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Notification Cooldown (minutes)"
                type="number"
                value={
                  preferences.notificationSettings?.notificationCooldown || 5
                }
                onChange={(e) =>
                  setPreferences((prev) => ({
                    ...prev,
                    notificationSettings: {
                      ...prev.notificationSettings!,
                      notificationCooldown: parseInt(e.target.value) || 5,
                    },
                  }))
                }
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Timezone"
                value={preferences.timezone}
                onChange={(e) =>
                  setPreferences((prev) => ({
                    ...prev,
                    timezone: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
